import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useCart } from '@/context/CartContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { 
  CheckCircle, 
  Loader2, 
  AlertCircle, 
  ArrowRight, 
  Calendar
} from 'lucide-react';
import { checkoutAPI } from '@/services/api';
import { toast } from 'sonner';

export default function CheckoutSuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const { refreshCart } = useCart();
  const [status, setStatus] = useState('loading');
  const [orderDetails, setOrderDetails] = useState(null);
  const pollRef = useRef(0);

  const sessionId = searchParams.get('session_id');
  const orderId = searchParams.get('order_id');

  useEffect(() => {
    // Auth-independent by design: the confirmation must render even if the session
    // didn't survive the external Stripe redirect (different origin, challenged first
    // request, expired token, etc.). We identify the order purely by order_id/session_id.
    if (sessionId) {
      localStorage.setItem('checkout_session_id', sessionId);
    }

    const activeSessionId = sessionId || localStorage.getItem('checkout_session_id');
    const activeOrderId = orderId;

    if (!activeSessionId && !activeOrderId) {
      setStatus('failed');
      return;
    }

    localStorage.removeItem('checkout_session_id');
    pollOrderStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pollOrderStatus = async () => {
    const maxAttempts = 15;
    const pollInterval = 2000;

    if (pollRef.current >= maxAttempts) {
      setStatus('pending');
      toast.info('Payment is still being processed. Please check your orders page later.');
      return;
    }

    try {
      let data;

      if (orderId) {
        const { cartAPI } = await import('@/services/api');
        // Public, auth-independent confirmation — verifies payment with Stripe and
        // fulfills idempotently. Does NOT require the user to be logged in.
        const response = await cartAPI.getOrderConfirmation(orderId);
        data = response.data;
      } else if (sessionId) {
        const response = await checkoutAPI.getOrderBySession(sessionId);
        data = response.data;
      }

      if (!data) {
        throw new Error('No data returned');
      }

      if (data.status === 'completed') {
        setOrderDetails(data);
        setStatus('success');
        toast.success('Payment successful! Thank you for your purchase.');
        refreshCart();
        return;
      } else if (data.status === 'cancelled' || data.status === 'refunded') {
        setStatus('expired');
        toast.error('Order was cancelled.');
        return;
      }

      // Still processing — keep polling
      pollRef.current += 1;
      setTimeout(pollOrderStatus, pollInterval);
    } catch (error) {
      console.error('Error checking order status:', error);
      if (pollRef.current < maxAttempts - 1) {
        pollRef.current += 1;
        setTimeout(pollOrderStatus, pollInterval);
      } else {
        setStatus('failed');
      }
    }
  };

  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-transparent pt-24 flex items-center justify-center" data-testid="checkout-success-page">
        <Card className="w-full max-w-md mx-4 border-zinc-200 shadow-xl rounded-2xl">
          <CardContent className="p-8 text-center">
            <Loader2 className="w-16 h-16 mx-auto text-zinc-900 animate-spin mb-6" />
            <h2 className="font-heading text-2xl font-bold text-zinc-950 mb-2">
              Processing Payment
            </h2>
            <p className="text-zinc-500">
              Please wait while we confirm your payment...
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen bg-transparent pt-24 pb-12 flex items-center justify-center" data-testid="checkout-success-page">
        <Card className="w-full max-w-lg mx-4 border-zinc-200 shadow-xl rounded-2xl overflow-hidden">
          <div className="bg-emerald-600 p-6 text-center">
            <div className="w-14 h-14 mx-auto bg-white rounded-full flex items-center justify-center mb-3 shadow-lg">
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
            <h1 className="font-heading text-2xl font-bold text-white mb-1">
              Payment Successful!
            </h1>
            <p className="text-green-100 text-sm">
              Thank you for your purchase
            </p>
          </div>

          <CardContent className="p-6">
            {orderDetails && (
              <div className="bg-zinc-50 rounded-lg p-4 mb-4">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-zinc-500 text-xs">Order Number</span>
                    <p className="font-mono text-zinc-700 font-medium">{orderDetails.order_number}</p>
                  </div>
                  <div className="text-right">
                    <span className="text-zinc-500 text-xs">Total</span>
                    <p className="font-semibold text-zinc-950">${(orderDetails.total_amount || 0).toFixed(2)}</p>
                  </div>
                </div>
              </div>
            )}

            <div className="flex items-center gap-3 p-3 bg-zinc-50 rounded-lg mb-4 text-sm">
              <Calendar className="w-5 h-5 text-zinc-900 flex-shrink-0" />
              <p className="text-zinc-600">
                <span className="font-medium text-zinc-950">Check your email</span> for webinar details and confirmation.
              </p>
            </div>

            <div className="flex gap-3">
              <Link to="/dashboard/webinars" className="flex-1">
                <Button className="w-full bg-zinc-950 hover:bg-zinc-800 text-white rounded-lg">
                  My Webinars
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/dashboard/orders" className="flex-1">
                <Button variant="outline" className="w-full border-zinc-300 rounded-lg">
                  View Orders
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === 'pending') {
    return (
      <div className="min-h-screen bg-transparent pt-24 flex items-center justify-center" data-testid="checkout-success-page">
        <Card className="w-full max-w-md mx-4 border-zinc-200 shadow-xl rounded-2xl">
          <CardContent className="p-8 text-center">
            <div className="w-20 h-20 mx-auto bg-yellow-100 rounded-full flex items-center justify-center mb-6">
              <Loader2 className="w-12 h-12 text-yellow-600" />
            </div>
            <h2 className="font-heading text-2xl font-bold text-zinc-950 mb-2">
              Payment Processing
            </h2>
            <p className="text-zinc-500 mb-6">
              Your payment is being processed. This may take a moment.
            </p>
            <Link to="/dashboard/orders">
              <Button className="bg-zinc-950 hover:bg-zinc-800 text-white rounded-xl px-6">
                Check My Orders
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-transparent pt-24 flex items-center justify-center" data-testid="checkout-success-page">
      <Card className="w-full max-w-md mx-4 border-zinc-200 shadow-xl rounded-2xl">
        <CardContent className="p-8 text-center">
          <div className="w-20 h-20 mx-auto bg-red-100 rounded-full flex items-center justify-center mb-6">
            <AlertCircle className="w-12 h-12 text-red-500" />
          </div>
          <h2 className="font-heading text-2xl font-bold text-zinc-950 mb-2">
            {status === 'expired' ? 'Session Expired' : 'Payment Issue'}
          </h2>
          <p className="text-zinc-500 mb-6">
            {status === 'expired' 
              ? 'Your payment session has expired. Please try again.'
              : 'We encountered an issue processing your payment. Please try again or contact support.'
            }
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/webinars">
              <Button className="w-full sm:w-auto bg-zinc-950 hover:bg-zinc-800 text-white rounded-xl px-6">
                Browse Webinars
              </Button>
            </Link>
            <Link to="/contact">
              <Button variant="outline" className="w-full sm:w-auto border-zinc-300 rounded-xl px-6">
                Contact Support
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
